/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { GridConfig } from '@coursera-web/cds-labs';

import { Grid, PageGridContainer } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import ProductCardCollection from 'bundles/collections-common/components/ProductCardCollection';
import ProductCardCollectionPlaceholder from 'bundles/collections-common/components/ProductCardCollectionPlaceholder';

const styles = {
  collectionSpacing: css`
    margin-top: -20px;
    margin-bottom: -20px;
  `,
};

type Props = {
  isCollectionLoading?: boolean;
  index?: number;
  collection?: CarouselCollection;
  customHeader?: React.ReactNode;
  itemsGridConfig?: GridConfig;
  customComponentAtIndex?: { index: number; component: React.JSX.Element };
  displayRows?: number;
  disableNegativeMargins?: boolean;
  disableCollectionPadding?: boolean;
  disableDesktopListView?: boolean;
  // This is used to determine if the component should be rendered in a two column layout in the home page
  twoColumnLayout?: boolean;
  additionalTrackingData?: {
    pageConfigComponent?: PageConfigComponentTrackingData;
  };
  dataTestId?: string;
};

const CollectionModule = ({
  isCollectionLoading,
  collection,
  customHeader,
  index,
  itemsGridConfig,
  customComponentAtIndex,
  displayRows,
  disableNegativeMargins,
  disableCollectionPadding,
  disableDesktopListView,
  twoColumnLayout,
  additionalTrackingData,
  dataTestId,
}: Props) => {
  if (isCollectionLoading) {
    const placeholderContent = (
      <Grid item xs={12}>
        <ProductCardCollectionPlaceholder />
      </Grid>
    );

    return twoColumnLayout ? placeholderContent : <PageGridContainer>{placeholderContent}</PageGridContainer>;
  }

  if (!collection?.entities?.length) {
    return null;
  }

  const collectionContent = (
    <Grid item xs={12} css={!disableNegativeMargins && styles.collectionSpacing}>
      <ProductCardCollection
        removeExtraPadding={disableCollectionPadding}
        collection={{
          ...collection,
          header: customHeader || collection.header,
        }}
        collectionIndex={index}
        itemsGridConfig={itemsGridConfig}
        customComponentAtIndex={customComponentAtIndex}
        displayRows={displayRows}
        disableDesktopListView={disableDesktopListView}
        eventingData={{ pageConfigComponent: additionalTrackingData?.pageConfigComponent }}
        data-testid={dataTestId}
      />
    </Grid>
  );

  return twoColumnLayout ? collectionContent : <PageGridContainer>{collectionContent}</PageGridContainer>;
};

export default CollectionModule;
